import type { StateVector } from "../math/state_vector";
import type { OrbitalElements } from "../math/orbital_elements";

type Listener<T extends unknown[]> = (...args: T) => void;

/**
 * Simple typed EventEmitter. No external dependencies.
 */
class TypedEvent<T extends unknown[]> {
  private listeners: Listener<T>[] = [];

  on(listener: Listener<T>): () => void {
    this.listeners.push(listener);
    return () => this.off(listener);
  }

  off(listener: Listener<T>): void {
    const idx = this.listeners.indexOf(listener);
    if (idx >= 0) this.listeners.splice(idx, 1);
  }

  fire(...args: T): void {
    for (const listener of this.listeners) {
      listener(...args);
    }
  }

  removeAll(): void {
    this.listeners = [];
  }
}

/**
 * All simulation events. The rendering layer subscribes to these.
 */
export class SimulationEvents {
  /** Spacecraft position/velocity updated. */
  readonly onSpacecraftMoved = new TypedEvent<[StateVector]>();

  /** Fuel fraction changed (0..1). */
  readonly onFuelFractionChanged = new TypedEvent<[number]>();

  /** Available delta-V changed (m/s). */
  readonly onDeltaVChanged = new TypedEvent<[number]>();

  /** Orbital elements changed (after maneuver or SOI transition). */
  readonly onOrbitChanged = new TypedEvent<[OrbitalElements]>();

  /** SOI transition occurred. Args: new body name, is entering (vs leaving). */
  readonly onSOIChanged = new TypedEvent<[string, boolean]>();

  /** A maneuver was executed. Args: delta-V magnitude (m/s), fuel consumed (kg). */
  readonly onManeuverExecuted = new TypedEvent<[number, number]>();

  /** Periapsis passage. Args: distance (km). */
  readonly onPeriapsisPassage = new TypedEvent<[number]>();

  /** Apoapsis passage. Args: distance (km). */
  readonly onApoapsisPassage = new TypedEvent<[number]>();

  /** A victory condition was met. Args: condition ID. */
  readonly onVictoryConditionMet = new TypedEvent<[string]>();

  /** A failure condition was met. Args: condition ID. */
  readonly onFailureConditionMet = new TypedEvent<[string]>();

  /** Crux completed. Args: crux ID, score (0-100), star rating (0-5). */
  readonly onCruxCompleted = new TypedEvent<[string, number, number]>();

  /** Simulation time changed. Args: Julian Date. */
  readonly onTimeChanged = new TypedEvent<[number]>();

  /** Pause state changed. */
  readonly onPauseChanged = new TypedEvent<[boolean]>();

  // Convenience fire methods

  fireSpacecraftMoved(state: StateVector): void {
    this.onSpacecraftMoved.fire(state);
  }

  fireFuelFractionChanged(fraction: number): void {
    this.onFuelFractionChanged.fire(fraction);
  }

  fireDeltaVChanged(dv: number): void {
    this.onDeltaVChanged.fire(dv);
  }

  fireOrbitChanged(elements: OrbitalElements): void {
    this.onOrbitChanged.fire(elements);
  }

  fireSOIChanged(bodyName: string, entering: boolean): void {
    this.onSOIChanged.fire(bodyName, entering);
  }

  fireManeuverExecuted(dvMag: number, fuelUsed: number): void {
    this.onManeuverExecuted.fire(dvMag, fuelUsed);
  }

  firePeriapsisPassage(distance: number): void {
    this.onPeriapsisPassage.fire(distance);
  }

  fireApoapsisPassage(distance: number): void {
    this.onApoapsisPassage.fire(distance);
  }

  fireVictoryConditionMet(conditionId: string): void {
    this.onVictoryConditionMet.fire(conditionId);
  }

  fireFailureConditionMet(conditionId: string): void {
    this.onFailureConditionMet.fire(conditionId);
  }

  fireCruxCompleted(cruxId: string, score: number, stars: number): void {
    this.onCruxCompleted.fire(cruxId, score, stars);
  }

  fireTimeChanged(jd: number): void {
    this.onTimeChanged.fire(jd);
  }

  firePauseChanged(paused: boolean): void {
    this.onPauseChanged.fire(paused);
  }
}
